import React, { useState, useEffect } from 'react';
import { BookOpen, ArrowRight, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ApiResponse {
  data: any;
  error?: string;
}

export function FinanceQuickBooks() {
  const [response, setResponse] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authStatus, setAuthStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    checkQuickBooksAuth();
  }, []);

  const checkQuickBooksAuth = async () => {
    try {
      const response = await fetch('https://www.innovationworks.com:49069/quickbooks/status', {
        credentials: 'include'
      });
      
      if (response.ok) {
        setAuthStatus('success');
      }
    } catch (err) {
      console.error('Error checking QuickBooks auth:', err);
    }
  };

  const handleAuthRedirect = () => {
    window.location.href = 'https://www.innovationworks.com:49069/quickbooks/auth';
  };

  const makeRequest = async (endpoint: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`https://www.innovationworks.com:49069/quickbooks/${endpoint}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      setResponse(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <BookOpen className="w-8 h-8 text-emerald-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">QuickBooks Integration</h1>
          </div>
          <Link
            to="/finance"
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Finance
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-end space-x-4 mb-6">
            <button
              onClick={handleAuthRedirect}
              className={`px-4 py-2 text-white rounded-md flex items-center ${
                authStatus === 'success'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              } disabled:opacity-50`}
            >
              {authStatus === 'success' ? (
                <>
                  Connected to QuickBooks
                  <CheckCircle className="w-4 h-4 ml-2" />
                </>
              ) : authStatus === 'error' ? (
                <>
                  Reconnect QuickBooks
                  <XCircle className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Connect to QuickBooks
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </button>
            {authStatus === 'success' && (
              <>
                <button
                  onClick={() => makeRequest('company')}
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                >
                  Company Info
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
                <button
                  onClick={() => makeRequest('customer')}
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                >
                  Customers
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </>
            )}
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-600">Loading QuickBooks data...</span>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {response && !loading && (
            <div className="bg-gray-50 rounded-lg p-4 overflow-auto max-h-[600px]">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}